/**
 * ORDER LIMIT
 *
 * Client-side validation for the per-product order limit (product.metafields.custom.max_order_quantity)
 * and the total order limit (shop.metafields.custom.max_order_quantity). Configuration is rendered by
 * snippets/order-limit.liquid; message colors/link come from the "Order limits" theme settings group.
 *
 * Uses document-level capture listeners so it runs before the theme's own handlers (ProductForm,
 * LineItemQuantity, QuantitySelector in theme.js) without modifying theme.js. This is a UX layer only —
 * the authoritative enforcement happens in the cart & checkout validation function.
 */
(function () {
  'use strict';

  const MESSAGE_DURATION = 5000;

  class OrderLimitGuard {
    constructor(config) {
      this.totalLimit = Number.isInteger(config.totalLimit) ? config.totalLimit : null;
      this.strings = config.strings || {};
      this.products = new Map(); // productId -> { max, multiplier } (multiplier = product-level fallback)
      this.variantMultipliers = new Map(); // variantId -> piece count (overrides the product-level value)
      this.lines = []; // { key, productId, variantId, quantity }
      this.lastValues = new WeakMap(); // quantity input -> last seen value, to react to increases only
      this._activeNote = null; // { violation, place, expiresAt } — re-placed after DOM swaps destroy the note

      (config.cartLines || []).forEach((line) => {
        this._registerProduct(line.productId, line.max, null);
        this._registerVariantMultiplier(line.variantId, line.multiplier); // already variant-resolved in Liquid
        this.lines.push({ key: line.key, productId: line.productId, variantId: line.variantId, quantity: line.quantity });
      });

      document.addEventListener('submit', this._onSubmit.bind(this), { capture: true });
      document.addEventListener('change', this._onChange.bind(this), { capture: true });
      document.addEventListener('click', this._onClick.bind(this), { capture: true });
      document.documentElement.addEventListener('cart:change', this._onCartChange.bind(this));

      // Re-scan after dynamic content swaps (cart drawer re-render, quick buy injection, variant re-render)
      this._observer = new MutationObserver(this._scheduleRefresh.bind(this));
      this._observer.observe(document.body, { childList: true, subtree: true });

      this._refresh();
    }

    /* ------------------------------------------------------------------ state */

    _registerProduct(productId, max, multiplier) {
      productId = parseInt(productId);
      if (!productId) {
        return;
      }

      max = parseInt(max);
      multiplier = parseInt(multiplier);

      const existing = this.products.get(productId);

      this.products.set(productId, {
        max: Number.isInteger(max) ? max : existing?.max ?? null,
        multiplier: Number.isInteger(multiplier) && multiplier > 1 ? multiplier : existing?.multiplier ?? 1
      });
    }

    _registerVariantMultiplier(variantId, multiplier) {
      variantId = parseInt(variantId);
      multiplier = parseInt(multiplier);

      if (variantId && Number.isInteger(multiplier) && multiplier >= 1) {
        this.variantMultipliers.set(variantId, multiplier);
      }
    }

    _productInfo(productId) {
      return this.products.get(productId) || { max: null, multiplier: 1 };
    }

    /**
     * Piece count of one unit: the variant metafield wins, the product-level value is the fallback.
     */
    _multiplierFor(productId, variantId) {
      return this.variantMultipliers.get(parseInt(variantId)) ?? this._productInfo(productId).multiplier;
    }

    /**
     * Pieces of the product already in the cart — pack-size variants count their piece value
     * (QA: a per-product limit of 50 allows only 4 twelve-packs, not 50).
     */
    _productUnits(productId) {
      return this.lines.reduce(
        (sum, line) => (line.productId === productId ? sum + line.quantity * this._multiplierFor(line.productId, line.variantId) : sum),
        0
      );
    }

    _totalUnits() {
      return this.lines.reduce((sum, line) => sum + line.quantity * this._multiplierFor(line.productId, line.variantId), 0);
    }

    _scanProducts() {
      document.querySelectorAll('form[is="product-form"][data-product-id]').forEach((form) => {
        this._registerProduct(form.dataset.productId, form.dataset.orderLimitMax, form.dataset.orderLimitMultiplier);

        // "variantId:count" pairs rendered by buy-buttons.liquid
        (form.dataset.orderLimitVariantMultipliers || '').split(/\s+/).forEach((pair) => {
          const [variantId, multiplier] = pair.split(':');
          this._registerVariantMultiplier(variantId, multiplier);
        });
      });

      document.querySelectorAll('line-item[data-product-id]').forEach((element) => {
        // the line's multiplier is variant-resolved in Liquid — register it per variant, not per product
        this._registerProduct(element.dataset.productId, element.dataset.orderLimitMax, null);
        this._registerVariantMultiplier(element.dataset.variantId, element.dataset.orderLimitMultiplier);
      });
    }

    _onCartChange(event) {
      const items = event.detail?.cart?.items;

      if (Array.isArray(items)) {
        this.lines = items.map((item) => ({ key: item.key, productId: item.product_id, variantId: item.variant_id, quantity: item.quantity }));
      }

      this._scheduleRefresh();
    }

    _scheduleRefresh() {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this._refresh(), 100);
    }

    _refresh() {
      this._scanProducts();
      this._updateQuantitySelectors();

      // The header note appears when an add was blocked — at that moment the cart is still
      // under the limit, so "under limit" is not staleness. It recovers when the cart total
      // DROPS below what it was when the note was shown; untracked orphans are swept.
      const headerNotes = document.querySelectorAll('.cart-drawer__top .order-limit-message');

      if (headerNotes.length) {
        const trackedTotalNote = this._activeNote && this._activeNote.violation.type === 'total' ? this._activeNote : null;
        const recovered = trackedTotalNote && this._totalUnits() < trackedTotalNote.totalUnitsAtShow;

        if (!trackedTotalNote || recovered) {
          headerNotes.forEach((message) => message.remove());

          if (trackedTotalNote) {
            this._activeNote = null;
          }
        }
      }

      // Section re-renders (e.g. the cart drawer swapping its inner HTML) destroy the note
      // mid-lifetime — re-place it until its time is up
      if (this._activeNote) {
        if (Date.now() >= this._activeNote.expiresAt) {
          this._activeNote = null;
        } else if (!document.querySelector('[data-order-limit-active]')) {
          this._activeNote.place(this._activeNote.violation, this._activeNote.expiresAt);
        }
      }
    }

    /**
     * Registers the note as active (so _refresh can restore it after DOM swaps) and shows it.
     * "place" receives (violation, expiresAt) and must resolve its anchors freshly on every call.
     */
    _notify(violation, scopeKey, place) {
      // Only one tracked note at a time — a new one replaces the previous wherever it is
      document.querySelectorAll('[data-order-limit-active]').forEach((message) => message.remove());
      this._activeNote = {
        violation,
        scopeKey,
        place,
        expiresAt: Date.now() + MESSAGE_DURATION,
        totalUnitsAtShow: this._totalUnits()
      };
      place(violation, this._activeNote.expiresAt);
    }

    /* ----------------------------------------------------------------- checks */

    /**
     * Checks if "quantity" more units of the given product/variant can be added; returns a violation or null.
     * "multiplier" is the piece count of one unit (resolve with _multiplierFor before calling).
     */
    _checkAdd(productId, quantity, multiplier) {
      const info = this._productInfo(productId);
      multiplier = multiplier || info.multiplier;

      if (info.max !== null && this._productUnits(productId) + quantity * multiplier > info.max) {
        return { type: 'product', limit: info.max };
      }

      if (this.totalLimit !== null && this._totalUnits() + quantity * multiplier > this.totalLimit) {
        return { type: 'total', limit: this.totalLimit };
      }

      return null;
    }

    /**
     * Checks the cart as a whole (used before checkout); returns a violation or null.
     */
    _checkCart() {
      const unitsByProduct = new Map();

      this.lines.forEach((line) => {
        const units = line.quantity * this._multiplierFor(line.productId, line.variantId);
        unitsByProduct.set(line.productId, (unitsByProduct.get(line.productId) || 0) + units);
      });

      for (const [productId, units] of unitsByProduct) {
        const info = this._productInfo(productId);

        if (info.max !== null && units > info.max) {
          return { type: 'product', limit: info.max, productId };
        }
      }

      if (this.totalLimit !== null && this._totalUnits() > this.totalLimit) {
        return { type: 'total', limit: this.totalLimit };
      }

      return null;
    }

    /**
     * Resolves the max value a product form's quantity input may reach given the rest of the cart.
     */
    _selectorContext(input) {
      const form = input.form;

      if (!form || form.getAttribute('is') !== 'product-form' || !form.dataset.productId) {
        return null;
      }

      const productId = parseInt(form.dataset.productId);
      const info = this._productInfo(productId);
      const variantId = parseInt(new FormData(form).get('id')); // current variant (picker input or hidden field)
      const multiplier = this._multiplierFor(productId, variantId);
      // allowances in packs: remaining pieces divided by the active variant's piece count
      const remainingProduct = info.max === null ? Infinity : Math.floor((info.max - this._productUnits(productId)) / multiplier);
      const remainingTotal = this.totalLimit === null ? Infinity : Math.floor((this.totalLimit - this._totalUnits()) / multiplier);

      return {
        form,
        allowance: Math.min(remainingProduct, remainingTotal),
        violationType: remainingProduct <= remainingTotal ? 'product' : 'total',
        violationLimit: remainingProduct <= remainingTotal ? info.max : this.totalLimit
      };
    }

    /**
     * Resolves the max value a cart line's quantity input may reach given the rest of the cart.
     */
    _lineContext(input) {
      const line = this.lines.find((cartLine) => cartLine.key === input.getAttribute('data-line-key'));

      if (!line) {
        return null;
      }

      const info = this._productInfo(line.productId);
      const multiplier = this._multiplierFor(line.productId, line.variantId);
      // allowances in packs: remaining pieces divided by this line's piece count
      const remainingProduct = info.max === null ? Infinity : Math.floor((info.max - this._productUnits(line.productId)) / multiplier);
      const remainingTotal = this.totalLimit === null ? Infinity : Math.floor((this.totalLimit - this._totalUnits()) / multiplier);

      return {
        line,
        allowance: line.quantity + Math.max(0, Math.min(remainingProduct, remainingTotal)),
        violationType: remainingProduct <= remainingTotal ? 'product' : 'total',
        violationLimit: remainingProduct <= remainingTotal ? info.max : this.totalLimit
      };
    }

    /* --------------------------------------------------------------- handlers */

    _onSubmit(event) {
      const form = event.target;

      // Add to cart (PDP, featured product, quick buy)
      if (form.getAttribute('is') === 'product-form' && form.dataset.productId) {
        const formData = new FormData(form);
        const quantity = parseInt(formData.get('quantity')) || 1;
        const productId = parseInt(form.dataset.productId);
        const variantId = parseInt(formData.get('id'));
        const violation = this._checkAdd(productId, quantity, this._multiplierFor(productId, variantId));

        if (violation) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this._showFormViolation(violation, form);
        }

        return;
      }

      // Checkout (cart drawer footer form, cart page form)
      const submitter = event.submitter || document.activeElement;

      if (form.action?.includes('/cart') && submitter?.name === 'checkout') {
        const violation = this._checkCart();

        if (violation) {
          event.preventDefault();
          event.stopImmediatePropagation();

          if (violation.type === 'product' && this._showOnProductLines(violation)) {
            return;
          }

          const formId = form.id;

          this._notify(violation, 'checkout', (activeViolation, expiresAt) => {
            const liveForm = (formId && document.getElementById(formId)) || (form.isConnected ? form : null);

            if (!liveForm) {
              return;
            }

            const drawerTop = liveForm.closest('cart-drawer')?.querySelector('.cart-drawer__top');

            if (drawerTop) {
              this._showMessage(activeViolation, drawerTop, 'append', drawerTop, expiresAt);
            } else {
              this._showMessage(activeViolation, liveForm, 'before', liveForm.parentElement, expiresAt);
            }
          });
        }
      }
    }

    /**
     * Places a violation raised from a product form (PDP, quick buy, product cards):
     * before the buy buttons, or appended to the card for direct-add forms.
     */
    _showFormViolation(violation, form) {
      const formId = form.id;
      const productId = form.dataset.productId;

      this._notify(violation, `form:${formId || productId}`, (activeViolation, expiresAt) => {
        const liveForm = (formId && document.getElementById(formId)) ||
          (form.isConnected ? form : document.querySelector(`form[is="product-form"][data-product-id="${productId}"]`));

        if (!liveForm) {
          return;
        }

        const buyButtons = liveForm.querySelector('buy-buttons');

        if (buyButtons) {
          this._showMessage(activeViolation, buyButtons, 'before', liveForm, expiresAt);
        } else {
          // Direct add form on product cards (no buy-buttons wrapper): show on the card
          const card = liveForm.closest('.product-card, .horizontal-product') || liveForm;
          this._showMessage(activeViolation, card, 'append', card, expiresAt);
        }
      });
    }

    /**
     * Places a violation raised from a cart line: total-limit messages go to the cart drawer
     * header, per-product messages onto the line item card itself.
     */
    _showLineViolation(violation, input) {
      const lineKey = input.getAttribute('data-line-key');

      this._notify(violation, `line:${lineKey}`, (activeViolation, expiresAt) => {
        const liveInput = (input.isConnected ? input : document.querySelector(`[data-line-key="${lineKey}"]`));

        if (!liveInput) {
          return;
        }

        const drawerTop = liveInput.closest('cart-drawer')?.querySelector('.cart-drawer__top');

        if (activeViolation.type === 'total' && drawerTop) {
          this._showMessage(activeViolation, drawerTop, 'append', drawerTop, expiresAt);
          return;
        }

        const row = liveInput.closest('.line-item, tr');
        const card = row?.matches('.line-item') ? row : row?.querySelector('.line-item');

        if (card) {
          this._showMessage(activeViolation, card, 'append', row, expiresAt);
        }
      });
    }

    /**
     * Renders a product violation inline on every cart line of the affected product.
     * Returns false when no line was found (caller falls back to the general placement).
     */
    _showOnProductLines(violation) {
      const lineElements = document.querySelectorAll(`line-item[data-product-id="${violation.productId}"]`);

      lineElements.forEach((lineElement) => {
        this._showMessage(violation, lineElement, 'append', lineElement.closest('tr') || lineElement);
      });

      return lineElements.length > 0;
    }

    _onChange(event) {
      const input = event.target;

      // selects participate too: dropdown variant pickers must re-trigger the greying evaluation
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) {
        return;
      }

      // Cart line quantity inputs (drawer + cart page); block before LineItemQuantity fetches cart/change.js
      if (input.hasAttribute('data-line-key')) {
        const line = this.lines.find((cartLine) => cartLine.key === input.getAttribute('data-line-key'));
        const newQuantity = parseInt(input.value);

        if (line && !isNaN(newQuantity) && newQuantity > line.quantity) {
          const violation = this._checkAdd(line.productId, newQuantity - line.quantity, this._multiplierFor(line.productId, line.variantId));

          if (violation) {
            event.stopImmediatePropagation();
            input.value = line.quantity;
            this._showLineViolation(violation, input);
          }
        }

        return;
      }

      // Product form quantity input: clamp increased values to the remaining allowance
      // (the native max attribute cannot express the total limit) and keep the plus button in sync.
      // Decreases are always allowed and never message.
      if (input.name === 'quantity') {
        const context = this._selectorContext(input);
        const value = parseInt(input.value);

        if (context && !isNaN(value)) {
          const lastValue = this.lastValues.get(input) ?? value;
          const min = parseInt(input.min) || 1;

          if (value > lastValue && value > context.allowance) {
            input.value = context.allowance >= min ? context.allowance : lastValue;
            this._showFormViolation({ type: context.violationType, limit: context.violationLimit }, context.form);
          }

          this.lastValues.set(input, parseInt(input.value));
        }

        this._updateQuantitySelectors();
        return;
      }

      // Variant switch inside a product form changes the active piece count — re-evaluate greying
      if (input.form?.getAttribute('is') === 'product-form') {
        this._updateQuantitySelectors();
      }
    }

    _onClick(event) {
      const button = event.target.closest('quantity-selector button:last-of-type');

      if (!button) {
        return;
      }

      const input = button.closest('quantity-selector').querySelector('input');
      const isLineInput = input?.hasAttribute('data-line-key');
      const context = input && (isLineInput ? this._lineContext(input) : this._selectorContext(input));

      if (!context) {
        return;
      }

      const step = parseInt(input.step) || 1;
      const nextValue = (parseInt(input.value) || 0) + step;

      if (nextValue > context.allowance) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const violation = { type: context.violationType, limit: context.violationLimit };

        if (isLineInput) {
          this._showLineViolation(violation, input);
        } else {
          this._showFormViolation(violation, context.form);
        }
      }
    }

    /* --------------------------------------------------------------------- ui */

    _updateQuantitySelectors() {
      document.querySelectorAll('quantity-selector').forEach((selector) => {
        const input = selector.querySelector('input');
        const plusButton = selector.querySelector('button:last-of-type');
        let context = null;

        if (input && plusButton) {
          context = input.hasAttribute('data-line-key') ? this._lineContext(input) : this._selectorContext(input);
        }

        if (!context) {
          return;
        }

        if (!this.lastValues.has(input)) {
          this.lastValues.set(input, parseInt(input.value) || 1);
        }

        const atLimit = (parseInt(input.value) || 0) >= context.allowance;

        plusButton.classList.toggle('is-order-limit-max', atLimit);

        if (atLimit) {
          plusButton.setAttribute('aria-disabled', 'true');
        } else {
          plusButton.removeAttribute('aria-disabled');

          // Recovery: drop the note in this scope — and stop restoring it after DOM swaps.
          // The scope key also clears notes whose element was already destroyed externally.
          const messageScope = context.form || input.closest('.line-item, tr');
          const recovered = messageScope ? messageScope.querySelectorAll('.order-limit-message') : [];
          recovered.forEach((message) => message.remove());

          const scopeKey = context.form
            ? `form:${context.form.id || context.form.dataset.productId}`
            : `line:${input.getAttribute('data-line-key')}`;

          if (this._activeNote && (recovered.length || this._activeNote.scopeKey === scopeKey)) {
            this._activeNote = null;
          }
        }
      });
    }

    _buildMessage(violation) {
      const template = document.getElementById('order-limit-message-template');

      if (!template) {
        return null;
      }

      const messageElement = template.content.firstElementChild.cloneNode(true);
      const text = violation.type === 'product' ? this.strings.productLimitReached : this.strings.totalLimitReached;

      messageElement.querySelector('[data-order-limit-text]').textContent = (text || '').replace(/\{\{\s*max\s*\}\}/g, violation.limit);

      return messageElement;
    }

    _showMessage(violation, anchor, position, scope, expiresAt = null) {
      const messageElement = this._buildMessage(violation);

      if (!messageElement) {
        return;
      }

      (scope || document).querySelectorAll('.order-limit-message').forEach((message) => message.remove());

      if (expiresAt !== null) {
        messageElement.setAttribute('data-order-limit-active', '');
      }

      anchor[position](messageElement);

      const lifetime = expiresAt === null ? MESSAGE_DURATION : Math.max(0, expiresAt - Date.now());

      setTimeout(async () => {
        if (this._activeNote && Date.now() >= this._activeNote.expiresAt) {
          this._activeNote = null;
        }

        if (!messageElement.isConnected) {
          return;
        }

        await messageElement.animate({ opacity: [1, 0] }, { duration: 250, fill: 'forwards' }).finished;
        messageElement.remove();
      }, lifetime);
    }
  }

  function init() {
    const configElement = document.getElementById('order-limit-config');

    if (!configElement) {
      return;
    }

    try {
      new OrderLimitGuard(JSON.parse(configElement.textContent));
    } catch (error) {
      console.warn('Order limit: invalid configuration', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
